import { useState, useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';

// Payment history types
export type PaymentStatus = 'successful' | 'failed' | 'pending';
export type PlanType = 'monthly' | 'annual';

// Plan tier based on provider count
export type PlanTier = 'bronze' | 'silver' | 'gold';

export interface PaymentHistoryItem {
  id: string;
  payment_date: string;
  amount: number;
  plan_type: PlanType;
  provider_count: number;
  token_count: number;
  status: PaymentStatus;
  reference_id: string;
  invoice_id: string | null;
}

interface GetPaymentHistoryResponse {
  success: boolean;
  message: string;
  data: {
    payments: PaymentHistoryItem[];
    total: number;
  };
}

interface DownloadInvoiceResponse {
  success: boolean;
  message: string;
  data: {
    invoice: {
      id: string;
      invoice_number: string;
    };
    pdf_base64: string;
  };
}

interface UsePaymentHistoryReturn {
  isLoading: boolean;
  isDownloading: boolean;
  error: string | null;
  payments: PaymentHistoryItem[];
  total: number;
  getPaymentHistory: () => Promise<void>;
  downloadInvoice: (subscriptionId: string) => Promise<void>;
  getPlanTier: (providerCount: number) => PlanTier;
}

/**
 * Get plan tier based on provider count
 * Bronze: 1-5 providers
 * Silver: 6-20 providers
 * Gold: above 20 providers
 */
export const getPlanTier = (providerCount: number): PlanTier => {
  if (providerCount > 20) {
    return 'gold';
  } else if (providerCount > 5) {
    return 'silver';
  }
  return 'bronze';
};

export const getPlanTierInfo = (tier: PlanTier) => {
  switch (tier) {
    case 'gold':
      return {
        label: 'Gold',
        backgroundColor: '#FEF3C7',
        color: '#92400E',
      };
    case 'silver':
      return {
        label: 'Silver',
        backgroundColor: '#F3F4F6',
        color: '#374151',
      };
    case 'bronze':
    default:
      return {
        label: 'Bronze',
        backgroundColor: '#FED7AA',
        color: '#C2410C',
      };
  }
};

export const usePaymentHistory = (): UsePaymentHistoryReturn => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [payments, setPayments] = useState<PaymentHistoryItem[]>([]);
  const [total, setTotal] = useState<number>(0);

  const getPaymentHistory = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get<GetPaymentHistoryResponse>(API_ENDPOINTS.GET_PAYMENT_HISTORY);

      setPayments(response.data.data.payments);
      setTotal(response.data.data.total);
    } catch (err: any) {
      const message =
        err?.response?.data?.message || err.message || 'Failed to get payment history';
      setError(message);
      setPayments([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const downloadInvoice = useCallback(async (subscriptionId: string): Promise<void> => {
    setIsDownloading(true);
    setError(null);

    try {
      const response = await axiosInstance.get<DownloadInvoiceResponse>(
        `${API_ENDPOINTS.DOWNLOAD_INVOICE}/${subscriptionId}/download`
      );

      const { invoice, pdf_base64 } = response.data.data;

      // Convert base64 to blob and trigger download
      const byteCharacters = atob(pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err: any) {
      const message =
        err?.response?.data?.message || err.message || 'Failed to download invoice';
      setError(message);
      throw new Error(message);
    } finally {
      setIsDownloading(false);
    }
  }, []);

  return {
    isLoading,
    isDownloading,
    error,
    payments,
    total,
    getPaymentHistory,
    downloadInvoice,
    getPlanTier,
  };
};

export default usePaymentHistory;
